import { CountryCodeFieldComponent } from '../../country-code-field.component';
import { Component, inject, signal, DestroyRef } from '@angular/core';
import { formControl, Validators, formGroup, FormControlExtended, FormGroupExtended } from '@al00x/forms';
import { ButtonClickEvent, CacBaseDialogComponent, CacDialogLayoutComponent, CacButtonComponent, CacFormBuilderComponent, CacFieldComponent, formBuilder, ItemRecord } from '@controladad/ng-base';
import { UserEntity, CustomerAddressEntity, ProductEntity, VariantEntity, ProductDropdownItem, OrderSummaryResponse } from '@models';

import { OrderApiService, AddressApiService, UserApiService, AdminProductsApiService, AdminPlaceOrderItem } from '../../../services';
import { HouseTypeValues } from '../../../data';
import { BehaviorSubject, switchMap, take, tap, of } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LazyDropdownComponent, LazyDropdownItem } from '../../../components';
import { MatIconModule } from "@angular/material/icon";
import { ReactiveFormsModule } from '@angular/forms';
import { KwdCurrencyPipe } from '../../../pipes';

declare const snackbar$: any;

export interface AddOrderDialogData { }

export type AddOrderDialogResult = boolean;

export type OrderItemForm = {
  productId: FormControlExtended<number>;
  variantId: FormControlExtended<number | null>;
  quantity: FormControlExtended<number>;
  itemPrice: FormControlExtended<number>;
  totalPrice: FormControlExtended<number>;
};

export interface OrderItem {
  orderItemId?: number;
  productId: number;
  product?: ProductEntity;
  selectedVariant?: VariantEntity;
  form: FormGroupExtended<OrderItemForm>;
  loadingProduct: boolean;
  outOfStock: boolean;
}

@Component({
  selector: 'lib-add-order-dialog',
  standalone: true,
  imports: [
    CacDialogLayoutComponent,
    CacButtonComponent,
    CacFormBuilderComponent,
    CountryCodeFieldComponent,
    CacFieldComponent,
    LazyDropdownComponent,
    MatIconModule,
    ReactiveFormsModule,
    KwdCurrencyPipe,
  ],
  templateUrl: './add-order-dialog.component.html',
  styleUrl: './add-order-dialog.component.scss',
})
export class AddOrderDialogComponent
  extends CacBaseDialogComponent<AddOrderDialogData, AddOrderDialogResult> {
  private orderApi = inject(OrderApiService);
  private addressApi = inject(AddressApiService);
  private userApi = inject(UserApiService);
  private adminProductApi = inject(AdminProductsApiService);
  private destroyRef = inject(DestroyRef);

  // Step management
  currentStep = signal<number>(0);
  stepValidationErrors = signal<string[]>([]);
  submitError = signal<string | undefined>(undefined);

  // Steps configuration
  steps = signal([
    { label: 'Customer Information', key: 'customer' },
    { label: 'Items', key: 'items' },
    { label: 'Order Summary', key: 'summary' },
  ]);

  // State management - Customer & Address
  selectedCustomer$ = new BehaviorSubject<LazyDropdownItem | null>(null);
  customerAddresses$ = new BehaviorSubject<CustomerAddressEntity[]>([]);
  areaList$ = new BehaviorSubject<ItemRecord<number>[]>([]);
  loadingCustomerDetails = signal<boolean>(false);
  loadingCustomerAddresses = signal<boolean>(false);

  // State management - Order Items
  orderItems = signal<OrderItem[]>([]);

  // State management - Order Summary & Payment
  orderSummary = signal<OrderSummaryResponse | null>(null);
  loadingOrderSummary = signal<boolean>(false);
  selectedPaymentMethod = signal<number | null>(null);
  paymentMethodControl = formControl<number | null>(null, [Validators.required]);

  constructor() {
    super();

    // Listen to payment method control changes
    this.paymentMethodControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.selectedPaymentMethod.set(value);
      });
  }

  // Customer dropdown configuration (following coupon dialog pattern)
  customerApiAdapter = this.userApi.getCustomerAdapter({
    default: {
      filters: [
        { key: "status", value: 1 }
      ]
    }
  });
  customerItemMapper = (user: UserEntity): LazyDropdownItem => ({
    id: user.id,
    label: `${user.id} - ${user.email || user.phoneNumber || 'No contact'}`,
    value: user.id,
    subtitle: user.fullName || [user.firstName, user.lastName].filter(Boolean).join(' '),
  });

  // Product dropdown configuration
  productApiAdapter = this.adminProductApi.getProductNamesAdapter();
  productItemMapper = (product: ProductDropdownItem): LazyDropdownItem => ({
    id: product.id,
    label: product.name.english || product.name.arabic || 'Unnamed Product',
    value: product.id,
    subtitle: product.name.arabic && product.name.english !== product.name.arabic ? product.name.arabic : undefined,
  });

  // Customer information form (copied from order-detail-dialog)
  countryCode = formControl<string | null>('+965');
  customerForm = formBuilder({
    cols: 2,
    width: '100%',
    inputs: {
      customerFullName: {
        control: formControl<string>('', [Validators.required, Validators.maxLength(257)]),
        label: 'Full Name',
      },
      customerEmail: {
        control: formControl<string>('', [Validators.email]),
        label: 'Email',
      },
      customerPhoneNumber: {
        control: formControl<string>('', [Validators.required]),
        label: 'Phone Number',
      },
    },
  });

  // Address form (copied from order-detail-dialog)
  addressForm = formBuilder({
    cols: 3,
    width: '100%',
    inputs: {
      governorateId: {
        control: formControl<number | null>(null, [Validators.required]),
        label: 'Governorate',
        type: 'select',
        items: this.addressApi.getGovernorates(),
      },
      areaId: {
        control: formControl<number | null>(null, [Validators.required]),
        label: 'Area',
        type: 'select',
        items: this.areaList$,
      },
      address: {
        control: formControl<string>('', [Validators.required, Validators.maxLength(4096)]),
        label: 'Address',
        type: 'textarea',
      },
    },
    onInit: (controls) => {
      // Listen for changes to governorate
      controls.governorateId.valueChanges
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          switchMap((value) => {
            if (!value) return of([]);
            return this.addressApi.getAreas(value).pipe(take(1));
          }),
          tap((areas) => this.areaList$.next(areas))
        )
        .subscribe();
    },
  });

  // Step navigation methods
  canProceedToNextStep(): boolean {
    return this.validateStep(this.currentStep(), false);
  }

  canGoToPreviousStep(): boolean {
    return this.currentStep() > 0;
  }

  canSave(): boolean {
    return this.currentStep() === this.steps().length - 1;
  }

  nextStep(): void {
    if (this.canProceedToNextStep() && this.currentStep() < this.steps().length - 1) {
      const nextStepIndex = this.currentStep() + 1;
      this.currentStep.set(nextStepIndex);
      this.stepValidationErrors.set([]);

      // If moving to summary step, load order summary
      if (this.steps()[nextStepIndex]?.key === 'summary') {
        this.loadOrderSummary();
      }
    }
  }

  previousStep(): void {
    if (this.canGoToPreviousStep()) {
      this.currentStep.set(this.currentStep() - 1);
      this.stepValidationErrors.set([]);
    }
  }

  goToStep(stepIndex: number): void {
    if (stepIndex >= 0 && stepIndex < this.steps().length) {
      // Validate all previous steps before allowing navigation
      let canNavigate = true;
      for (let i = 0; i < stepIndex; i++) {
        if (!this.validateStep(i, false)) {
          canNavigate = false;
          break;
        }
      }

      if (canNavigate) {
        this.currentStep.set(stepIndex);
        this.stepValidationErrors.set([]);
      }
    }
  }

  validateCurrentStep(): boolean {
    return this.validateStep(this.currentStep(), true);
  }

  /**
   * Validates a step with optional signal update
   * @param stepIndex - The index of the step to validate
   * @param updateSignal - Whether to update the stepValidationErrors signal (default: true)
   */
  validateStep(stepIndex: number, updateSignal = true): boolean {
    const errors: string[] = [];
    const currentSteps = this.steps();

    if (stepIndex >= currentSteps.length) {
      return true;
    }

    const stepKey = currentSteps[stepIndex].key;
    let isValid = false;

    switch (stepKey) {
      case 'customer':
        isValid = this.validateCustomerStep(errors);
        break;
      case 'items':
        isValid = this.validateItemsStep(errors);
        break;
      case 'summary':
        isValid = this.validateSummaryStep(errors);
        break;
      default:
        isValid = true;
        break;
    }

    // Update validation errors signal only if requested
    if (updateSignal) {
      this.stepValidationErrors.set(errors);
    }
    return isValid;
  }

  private validateCustomerStep(errors: string[]): boolean {
    this.customerForm.formGroup.markAllAsTouched();
    this.addressForm.formGroup.markAllAsTouched();

    const customerData = this.customerForm.getValue();
    const addressData = this.addressForm.getValue();

    if (!customerData || !addressData) {
      errors.push('Please fill all required fields');
      return false;
    }

    if (this.customerForm.formGroup.invalid || this.addressForm.formGroup.invalid) {
      errors.push('Please fix validation errors');
      return false;
    }

    return true;
  }

  private validateItemsStep(errors: string[]): boolean {
    this.orderItems().forEach((item) => {
      item.form.markAllAsTouched();
    });

    const currentItems = this.orderItems();

    if (currentItems.length === 0) {
      errors.push('Please add at least one item');
      return false;
    }

    const areItemsValid = currentItems.every((item) => item.form.valid);
    if (!areItemsValid) {
      errors.push('Please fix validation errors in order items');
      return false;
    }

    const hasOutOfStockItems = currentItems.some((item) => item.outOfStock);
    if (hasOutOfStockItems) {
      errors.push('Please remove out of stock items');
      return false;
    }

    return true;
  }

  private validateSummaryStep(errors: string[]): boolean {
    if (!this.selectedPaymentMethod()) {
      errors.push('Please select a payment method');
      return false;
    }

    if (!this.orderSummary()) {
      errors.push('Order summary is loading...');
      return false;
    }

    return true;
  }

  onCustomerSelectionChange(selectedCustomer: LazyDropdownItem | LazyDropdownItem[] | null): void {
    // Handle single selection only
    const customer = Array.isArray(selectedCustomer) ? selectedCustomer[0] : selectedCustomer;

    this.selectedCustomer$.next(customer);

    // Always clear previous customer data first
    this.customerAddresses$.next([]);
    this.clearCustomerForm();

    if (customer) {
      // Load customer details and addresses
      this.loadCustomerDetails(customer.id);
      this.loadCustomerAddresses(customer.id);
    }
  }

  private loadCustomerDetails(customerId: number): void {
    this.loadingCustomerDetails.set(true);
    this.userApi.getCustomerDetails(customerId).subscribe({
      next: (response) => {
        const customer = response.customerDetailsDTO;
        this.countryCode.setValue(customer.countryCode ?? null);
        this.customerForm.patchValue({
          customerFullName: customer.fullName || [customer.firstName, customer.lastName].filter(Boolean).join(' '),
          customerEmail: customer.email || '',
          customerPhoneNumber: customer.phoneNumber || '',
        });
        this.loadingCustomerDetails.set(false);
      },
      error: (error) => {
        console.error('Error loading customer details:', error);
        snackbar$.show('Error loading customer details. Please fill the information manually.');
        this.loadingCustomerDetails.set(false);
      },
    });
  }

  private loadCustomerAddresses(customerId: number): void {
    this.loadingCustomerAddresses.set(true);
    this.addressApi.getAll(customerId).subscribe({
      next: (addresses) => {
        this.customerAddresses$.next(addresses);
        this.loadingCustomerAddresses.set(false);
      },
      error: (error) => {
        console.error('Error loading customer addresses:', error);
        snackbar$.show('Error loading customer addresses. Please enter the address manually.');
        this.customerAddresses$.next([]);
        this.loadingCustomerAddresses.set(false);
      },
    });
  }

  private clearCustomerForm(): void {
    this.customerForm.formGroup.reset({
      customerFullName: '',
      customerEmail: '',
      customerPhoneNumber: '',
    });
    this.clearAddressForm();
  }

  onAddressSelectionChange(event: Event): void {
    const addressId = parseInt((event.target as HTMLSelectElement).value);
    if (!addressId) {
      this.clearAddressForm();
      return;
    }

    const selectedAddress = this.customerAddresses$.value.find(addr => addr.id === addressId);
    if (!selectedAddress) return;

    this.populateAddressForm(selectedAddress);
  }

  private populateAddressForm(address: CustomerAddressEntity): void {
    const areaId = address.areaId || address.area?.id;
    const governorateId = address.governorateId || address.governorate?.id;

    // Patch all fields except governorate/area
    this.addressForm.patchValue({
      address: address.address || '',
    });

    // Load areas and update governorate/area
    if (governorateId) {
      this.addressApi.getAreas(governorateId)
        .pipe(take(1))
        .subscribe({
          next: (areas) => {
            this.areaList$.next(areas);
            this.addressForm.patchValue({
              governorateId,
              areaId,
            });
          },
          error: (error) => console.error('Error loading areas:', error)
        });
    } else {
      this.areaList$.next([]);
      this.addressForm.patchValue({ governorateId, areaId: null });
    }
  }

  private clearAddressForm(): void {
    this.areaList$.next([]);
    this.addressForm.formGroup.reset({
      governorateId: null,
      areaId: null,
      address: '',
    });
  }

  // Address display utility
  getAddressDisplayText(address: CustomerAddressEntity): string {
    if (address.address?.trim()) {
      return [address.governorate?.name?.english, address.area?.name?.english, address.address]
        .filter(Boolean).join(' - ');
    }
    const parts = [
      address.block,
      address.street,
      address.avenue,
      address.buildingNumber,
    ].filter(part => part && part.trim() !== '');

    return parts.join(' - ') || 'Address';
  }

  /**
   * Helper method to update an order item by index
   */
  private updateOrderItemByIndex(index: number, updates: Partial<OrderItem>): void {
    this.orderItems.update((items) => {
      const updatedItems = [...items];
      if (updatedItems[index]) {
        updatedItems[index] = { ...updatedItems[index], ...updates };
      }
      return updatedItems;
    });
  }

  /**
   * Helper method to update an order item by reference
   */
  private updateOrderItem(orderItem: OrderItem, updates: Partial<OrderItem>): void {
    this.orderItems.update((items) => {
      const index = items.findIndex(item => item === orderItem);
      if (index >= 0) {
        const updatedItems = [...items];
        updatedItems[index] = { ...updatedItems[index], ...updates };
        return updatedItems;
      }
      return items;
    });
  }

  /**
   * Calculate and update prices for an order item
   */
  private updateOrderItemPrices(orderItem: OrderItem, quantity?: number): void {
    const variant = orderItem.selectedVariant;
    if (!variant) {
      orderItem.form.patchValue({
        itemPrice: 0,
        totalPrice: 0
      }, { emitEvent: false });
      return;
    }

    const itemPrice = variant.salePrice || variant.regularPrice;
    const qty = quantity ?? orderItem.form.controls.quantity.value ?? 1;
    const totalPrice = itemPrice * qty;

    orderItem.form.patchValue({
      itemPrice,
      totalPrice
    }, { emitEvent: false });
  }

  onProductSelectionChange(selectedProduct: LazyDropdownItem | LazyDropdownItem[] | null): void {
    const product = Array.isArray(selectedProduct) ? selectedProduct[0] : selectedProduct;

    if (!product) {
      return;
    }

    this.createOrderItemForm(product.id);
  }

  createOrderItemForm(productId: number): void {
    const form = formGroup({
      productId: formControl<number>(productId, [Validators.required]),
      variantId: formControl<number | null>(null),
      quantity: formControl<number>(1, [Validators.required, Validators.min(1)]),
      itemPrice: formControl<number>(0),
      totalPrice: formControl<number>(0),
    });


    const orderItem: OrderItem = {
      productId: productId,
      form,
      loadingProduct: true,
      outOfStock: false
    };

    // Disable quantity while loading
    form.controls.quantity.disable();

    this.orderItems.update((items) => [...items, orderItem]);

    // Load product details
    this.loadProductDetails(productId, this.orderItems().length - 1);
  }

  removeOrderItem(index: number): void {
    this.orderItems.update((items) => items.filter((_, idx) => idx !== index));
  }

  private loadProductDetails(productId: number, itemIndex: number): void {
    this.adminProductApi.get(productId).subscribe({
      next: (product) => {
        const isOutOfStock = this.isProductOutOfStock(product);
        this.updateOrderItemByIndex(itemIndex, {
          product,
          loadingProduct: false,
          outOfStock: isOutOfStock
        });

        const item = this.orderItems()[itemIndex];
        if (item) {
          // Setup form value changes and initial variant selection
          this.setupOrderItemFormListeners(item);
          this.selectInitialVariant(item, product);

          // Handle disabled state based on product type and stock
          if (isOutOfStock) {
            item.form.controls.quantity.disable();
          } else if (product.type !== 1) {
            item.form.controls.quantity.enable();
          } else {
            item.form.controls.quantity.disable();
          }
        }
      },
      error: (error) => {
        console.error('Error loading product details:', error);
        snackbar$.show('Error loading product details.');
        this.updateOrderItemByIndex(itemIndex, {
          loadingProduct: false,
          outOfStock: true
        });
        this.orderItems()[itemIndex]?.form.controls.quantity.disable();
      },
    });
  }

  /**
   * Check if a product is completely out of stock
   * For variable products, returns true only if ALL variants are out of stock
   * For single-variant products, checks the default variant
   */
  private isProductOutOfStock(product: ProductEntity): boolean {
    if (product.type === 1) {
      return !product.variants.some(variant =>
        !variant.isManageableStock || variant.stock > 0
      );
    } else {
      const variant = product.variants[0];
      return variant && variant.isManageableStock && variant.stock <= 0;
    }
  }

  private selectInitialVariant(orderItem: OrderItem, product: ProductEntity): void {
    if (product.type !== 1 && product.variants.length > 0) {
      // Simple and bundle products expose one sellable variant.
      const variant = product.variants[0];
      const quantity = orderItem.form.controls.quantity.value || 1;

      this.updateOrderItem(orderItem, { selectedVariant: variant });
      orderItem.form.patchValue({
        variantId: variant.id
      });

      this.updateOrderItemPrices(orderItem, quantity);
      this.updateQuantityValidation(orderItem);
    }
  }

  onVariantSelectionChange(orderItem: OrderItem, variantId: number): void {
    const variant = orderItem.product?.variants.find(v => v.id === variantId);

    if (variant) {
      const currentQuantity = orderItem.form.controls.quantity.value || 1;
      const isOutOfStock = variant.isManageableStock && variant.stock <= 0;

      this.updateOrderItem(orderItem, {
        selectedVariant: variant,
        outOfStock: isOutOfStock
      });

      orderItem.form.patchValue({ variantId: variant.id });
      this.updateOrderItemPrices(orderItem, currentQuantity);
      this.updateQuantityValidation(orderItem);

      // Handle form control state
      if (isOutOfStock) {
        orderItem.form.controls.quantity.disable();
      } else {
        orderItem.form.controls.quantity.enable();
      }
    } else {
      // No variant selected
      this.updateOrderItem(orderItem, { selectedVariant: undefined });
      orderItem.form.patchValue({
        variantId: null,
        itemPrice: 0,
        totalPrice: 0
      });
      orderItem.form.controls.quantity.disable();
    }
  }

  /**
   * Update quantity validators - Admin can add any quantity
   * No stock limits enforced
   */
  private updateQuantityValidation(orderItem: OrderItem): void {
    const quantityControl = orderItem.form.controls.quantity;

    // Admin can add any quantity regardless of stock
    quantityControl.setValidators([Validators.required, Validators.min(1)]);
    quantityControl.updateValueAndValidity();
  }

  private setupOrderItemFormListeners(orderItem: OrderItem): void {
    orderItem.form.controls.quantity.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((quantity) => {
        if (!quantity) return;

        const variant = orderItem.selectedVariant;
        if (!variant) {
          orderItem.form.patchValue({ itemPrice: 0, totalPrice: 0 }, { emitEvent: false });
          return;
        }

        // Admin can add any quantity - no stock limit enforcement
        this.updateOrderItemPrices(orderItem, quantity);
      });
  }

  getVariantDisplayText(variant: VariantEntity): string {
    if (!variant.attributes || variant.attributes.length === 0) {
      return 'Default';
    }

    return variant.attributes
      .map(attr => `${attr.attributeName.english || attr.attributeName.arabic}-${attr.term.english || attr.term.arabic}`)
      .join(' - ');
  }

  getCurrentVariant(orderItem: OrderItem): VariantEntity | null {
    const product = orderItem.product;
    return orderItem.selectedVariant ||
      (product && product.type !== 1 && product.variants.length > 0
        ? product.variants[0]
        : null);
  }

  getItemPrice(orderItem: OrderItem): number {
    const variant = this.getCurrentVariant(orderItem);
    return variant ? (variant.salePrice || variant.regularPrice) : 0;
  }

  getTotalPrice(orderItem: OrderItem): number {
    const itemPrice = this.getItemPrice(orderItem);
    const quantity = orderItem.form.controls.quantity.value || 1;
    const totalPrice = itemPrice * quantity;

    // Sync form controls
    if (orderItem.form.controls.totalPrice.value !== totalPrice) {
      orderItem.form.controls.totalPrice.patchValue(totalPrice, { emitEvent: false });
    }
    if (orderItem.form.controls.itemPrice.value !== itemPrice) {
      orderItem.form.controls.itemPrice.patchValue(itemPrice, { emitEvent: false });
    }

    return totalPrice;
  }

  getSelectedVariantIds(): number[] {
    return this.orderItems()
      .filter(item => item.selectedVariant)
      .map(item => item.selectedVariant!.id);
  }

  getAvailableVariants(orderItem: OrderItem): VariantEntity[] {
    if (!orderItem.product || orderItem.product.type !== 1) {
      return [];
    }

    const selectedVariantIds = this.getSelectedVariantIds();
    const currentItemVariantId = orderItem.selectedVariant?.id;

    // Filter out already selected variants, but allow the current item's selected variant
    return orderItem.product.variants.filter(variant =>
      !selectedVariantIds.includes(variant.id) || variant.id === currentItemVariantId
    );
  }

  onMainAction(event: ButtonClickEvent): void {
    if (this.canSave()) {
      this.onSubmitOrder(event);
    } else {
      if (!this.validateCurrentStep()) {
        const errors = this.stepValidationErrors();
        if (errors.length > 0) {
          snackbar$.show(errors.join('. '));
        }
        event.setLoading(false);
        return;
      }
      this.nextStep();
      event.setLoading(false);
    }
  }

  private onSubmitOrder(event: ButtonClickEvent): void {
    // Validate all steps
    for (let i = 0; i < this.steps().length; i++) {
      if (!this.validateStep(i, true)) {
        event.setLoading(false);
        this.currentStep.set(i);
        return;
      }
    }

    const orderData = this.prepareOrderData();
    if (!orderData) {
      event.setLoading(false);
      return;
    }

    this.orderApi.adminPlaceOrder(orderData)
      .pipe(event.pipe())
      .subscribe({
        next: (response) => {
          const orderId = response?.orderId || response?.id;
          snackbar$.show(`Order created successfully${orderId ? ` (Order #${orderId})` : ''}`);
          this.submit(true);
        },
      });
  }

  private prepareOrderData() {
    const customerData = this.customerForm.getValue();
    const addressData = this.addressForm.getValue();
    const paymentMethodId = this.selectedPaymentMethod();

    if (!customerData || !addressData || !paymentMethodId) {
      snackbar$.show('Please complete all required fields');
      return null;
    }

    return {
      targetUserId: this.selectedCustomer$.value?.id,
      address: {
        governorateId: addressData.governorateId!,
        areaId: addressData.areaId!,
        address: addressData.address,
      },
      paymentMethodId,
      items: this.prepareOrderItemsForSubmission(),
      customerFullName: customerData.customerFullName,
      customerCountryCode: this.countryCode.value,
      customerEmail: customerData.customerEmail || null,
      customerPhoneNumber: customerData.customerPhoneNumber,
    };
  }

  private prepareOrderItemsForSubmission(): AdminPlaceOrderItem[] {
    return this.orderItems().map(item => {
      const variant = this.getCurrentVariant(item);
      if (!variant) {
        throw new Error(`No variant selected for product ${item.productId}`);
      }
      return {
        variantId: variant.id,
        countOfOrder: item.form.controls.quantity.value || 1,
        isGift: false
      };
    });
  }

  private loadOrderSummary(): void {
    this.loadingOrderSummary.set(true);
    this.orderSummary.set(null);
    this.selectedPaymentMethod.set(null);
    this.paymentMethodControl.patchValue(null);

    const selectedCustomer = this.selectedCustomer$.value;
    const addressData = this.addressForm.getValue();

    const request = {
      targetUserId: selectedCustomer?.id,
      governorateId: addressData?.governorateId,
      items: this.prepareOrderItemsForSubmission()
    };

    this.orderApi.previewCheckout(request).subscribe({
      next: (response) => {
        const summary: OrderSummaryResponse = {
          prices: {
            itemsPrice: response.summary.finalSubTotal,
            itemsDiscount: response.summary.totalSaved || 0,
            shippingFee: response.summary.finalShippingCost,
            shippingFeeDiscount: 0,
            totalCouponCodeDiscount: response.summary.totalCouponCodeDiscount || 0,
            totalOrderPrice: response.summary.total
          },
          paymentMethods: response.paymentMethods || [],
          walletBalance: response.walletBalance
        };

        this.orderSummary.set(summary);
        this.loadingOrderSummary.set(false);
      },
      error: (error) => {
        console.error('Error loading order summary:', error);
        snackbar$.show('Error loading order summary. Please try again.');
        this.loadingOrderSummary.set(false);
      }
    });
  }

  getPaymentMethodDisplayOptions(): ItemRecord<number>[] {
    const summary = this.orderSummary();
    if (!summary) return [];

    // Payment methods come from API as PaymentMethodViewDTO[] { id, name, image }
    return summary.paymentMethods.map(method => ({
      label: method.name,
      value: method.id
    }));
  }

  closeDialog(): void {
    this.submit(false);
  }
}
